import { EventEmitter, Injectable, Output } from '@angular/core';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { BreakpointType, ScreenSizeType } from '@models/responsive';

@Injectable({
    providedIn: 'root',
})
export class ResponsiveService {
    private _screenSize = ScreenSizeType.Unknown;
    private _deviceAndOrientation = BreakpointType.Unknown;

    @Output() breakPoinEmitter: EventEmitter<ScreenSizeType> = new EventEmitter();

    private readonly screenSizeBreakPoints = new Map([
        [Breakpoints.XLarge, ScreenSizeType.XLarge],
        [Breakpoints.Large, ScreenSizeType.Large],
        [Breakpoints.Medium, ScreenSizeType.Medium],
        [Breakpoints.Small, ScreenSizeType.Small],
        [Breakpoints.XSmall, ScreenSizeType.XSmall],
    ]);

    private readonly deviceAndOrientations = new Map([
        [Breakpoints.HandsetLandscape, BreakpointType.HandsetLandscape],
        [Breakpoints.HandsetPortrait, BreakpointType.HandsetPortrait],
        [Breakpoints.TabletLandscape, BreakpointType.TabletLandscape],
        [Breakpoints.TabletPortrait, BreakpointType.TabletPortrait],
        [Breakpoints.WebLandscape, BreakpointType.WebLandscape],
        [Breakpoints.WebPortrait, BreakpointType.WebPortrait],
    ]);

    constructor(breakpointObserver: BreakpointObserver) {
        this.checkDeviceAndOrientaion(breakpointObserver);
        this.checkScreenSize(breakpointObserver);
    }

    private checkDeviceAndOrientaion(breakpointObserver: BreakpointObserver) {
        breakpointObserver
            .observe([
                Breakpoints.HandsetLandscape,
                Breakpoints.HandsetPortrait,
                Breakpoints.TabletLandscape,
                Breakpoints.TabletPortrait,
                Breakpoints.WebLandscape,
                Breakpoints.WebPortrait,
            ])
            .subscribe((result: any) => {
                for (const query of Object.keys(result.breakpoints)) {
                    if (result.breakpoints[query]) {
                        this._deviceAndOrientation =
                            this.deviceAndOrientations.get(query) ?? BreakpointType.Unknown;
                    }
                }
            });
    }

    private checkScreenSize(breakpointObserver: BreakpointObserver) {
        breakpointObserver
            .observe([
                Breakpoints.XLarge,
                Breakpoints.Large,
                Breakpoints.Medium,
                Breakpoints.Small,
                Breakpoints.XSmall,
            ])
            .subscribe((result: any) => {
                for (const query of Object.keys(result.breakpoints)) {
                    if (result.breakpoints[query]) {
                        this._screenSize =
                            this.screenSizeBreakPoints.get(query) ?? ScreenSizeType.Unknown;
                        this.breakPoinEmitter.emit(this.screenSizeBreakPoints.get(query));
                    }
                }
            });
    }

    public get screenSize() {
        return this._screenSize;
    }

    public get deviceAndOrientation() {
        return this._deviceAndOrientation;
    }

    public isXLarge() {
        return this._screenSize === ScreenSizeType.XLarge;
    }

    public isLarge() {
        return this._screenSize === ScreenSizeType.Large;
    }

    public isMedium() {
        return this._screenSize === ScreenSizeType.Medium;
    }

    public isSmall() {
        return this._screenSize === ScreenSizeType.Small;
    }

    public isXSmall() {
        return this._screenSize === ScreenSizeType.XSmall;
    }

    public isHandsetLandscape() {
        return this._deviceAndOrientation === BreakpointType.HandsetLandscape;
    }

    public isHandsetPortrait() {
        return this._deviceAndOrientation === BreakpointType.HandsetPortrait;
    }

    public isTabletLandscape() {
        return this._deviceAndOrientation === BreakpointType.TabletLandscape;
    }

    public isTabletPortrait() {
        return this._deviceAndOrientation === BreakpointType.TabletPortrait;
    }

    public isWebLandscape() {
        return this._deviceAndOrientation === BreakpointType.WebLandscape;
    }

    public isWebPortrait() {
        return this._deviceAndOrientation === BreakpointType.WebPortrait;
    }

    public isHandset() {
        return this.isHandsetPortrait() || this.isHandsetLandscape();
    }
}
